import { InfinityIcon, LockIcon, WifiIcon } from "@xola/icons";
import { Badge, Popover, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { PurchaseItemStatus } from "common";
import { isEmpty, sumBy } from "lodash";
import React from "react";
import { Event, OPEN_UNLIMITED, Permission, PurchaseItem, Timeslot, UnitType } from "types";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SkeletonLine } from "../../Common/Skeleton/TableSkeleton";
import { BalanceBadge } from "../Common/BalanceBadge";
import { PendingBadgeCount } from "../PendingBadgeCount";
import { DemographicPopoverList } from "./DemographicPopoverList";
import { useExperience } from "@/hooks/resources/useProduct";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useResource } from "@/hooks/useResource";
import { isAlwaysPrivateExperience, isVirtualExperience } from "@/models/Experience";
import { getCustomerName } from "@/models/Purchase";
import { getShortCode } from "@/models/PurchaseItem";
import {
    canOpenRoster,
    getBookedCount,
    getHoldStatusCount,
    getOpenCapacity,
    hasWaitlists,
    isEquipmentTimeslot,
} from "@/models/Timeslot";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardScope } from "@/types/Dashboard";

interface CapacityBalancePrivacyDisplayProps {
    timeslot: Timeslot;
}

export const CapacityBalancePrivacyDisplay = ({ timeslot }: CapacityBalancePrivacyDisplayProps) => {
    const { isGranted } = useAuthorization();
    const scope = useDashboardFilterStore((state) => state.filters.scope);
    const { data: experience } = useExperience(timeslot.product);
    const { data: event } = useResource<Event>(["events", timeslot.event?.id]);

    const purchaseItemReferences = event?.purchaseItems ?? [];
    const latestPurchaseItemId = purchaseItemReferences[purchaseItemReferences.length - 1]?.id;

    const { data: purchaseItem } = useResource<PurchaseItem>(["purchaseItems", latestPurchaseItemId], null, {
        enabled: !!(event?.private && isGranted(Permission.PURCHASES_VIEW) && latestPurchaseItemId),
    });

    if (!experience) {
        return (
            <div className="flex w-1/4 items-center space-x-4">
                <SkeletonLine />
                <SkeletonLine />
            </div>
        );
    }

    const isOnlyOneCatalogItem =
        experience.catalog.items.filter((catalogItem) => catalogItem.unitType === UnitType.DEMOGRAPHIC).length === 1;

    const { aggregatedBalance = 0, statusCounts = [] } = timeslot;

    const isVirtual = (!!event?.virtualMeeting || isVirtualExperience(experience)) ?? false;
    const isPrivate = event?.private ?? (experience && isAlwaysPrivateExperience(experience));
    const isGratedDemographicView = isGranted(Permission.DEMOGRAPHIC_LIST_VIEW);
    const isEquipment = isEquipmentTimeslot(timeslot);
    const canViewReservedBreakdown = isEquipment
        ? isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT)
        : isGratedDemographicView && !isOnlyOneCatalogItem;

    const pendingCount =
        sumBy(statusCounts, (statusCount) =>
            statusCount.status === PurchaseItemStatus.PENDING_ACCEPT ||
            statusCount.status === PurchaseItemStatus.PENDING_MINIMUM
                ? statusCount.count
                : 0,
        ) ?? 0;

    if (!timeslot.capacity) {
        return null;
    }

    // Equipment slots show unit counts (handled inside the capacity helpers); other slots show guest counts.
    const available = getOpenCapacity(timeslot);

    const customerName = getCustomerName(timeslot) || undefined;
    const name = purchaseItem && isEmpty(customerName) ? getShortCode(purchaseItem)?.toUpperCase() : customerName;
    const purchaseItemStatus = purchaseItem?.status;

    const totalReserved = getBookedCount(timeslot);
    const holdStatusCount = getHoldStatusCount(statusCounts) ?? 0;

    const availableCount = (
        <span className={clsx("ui-dashboard-available", available < 0 && "text-danger")}>
            <var data-var="availability">
                {available >= OPEN_UNLIMITED ? <InfinityIcon data-testid="ui-dashboard-infinity-icon" /> : available}
            </var>
            &nbsp;Available
        </span>
    );

    return (
        <div className="flex flex-col space-y-1 sm:w-72 xl:w-100">
            {/* Available, reserved, waitlist */}
            <div className="h-4.5 space-x-2">
                {timeslot.unavailability?.message && canOpenRoster(timeslot) ? (
                    <Tooltip
                        content={
                            <NoTranslate className="whitespace-pre-line">{timeslot.unavailability.message}</NoTranslate>
                        }
                    >
                        {availableCount}
                    </Tooltip>
                ) : (
                    availableCount
                )}

                <span>
                    <span className="mr-2 h-1.5 w-1.5 text-[#CDC9D3]">&bull;</span>
                    <Popover disabled={totalReserved < 1 || !canViewReservedBreakdown}>
                        <span data-testid="ui-dashboard-reserved-count">
                            <var
                                data-var="totalReserved"
                                className={clsx(totalReserved > 0 && "underline decoration-dashed")}
                            >
                                {totalReserved}
                            </var>
                            <span className={clsx("ml-1", totalReserved > 0 && "underline decoration-dashed")}>
                                Reserved
                            </span>
                        </span>
                        <Popover.Content className="w-50 cursor-default space-y-2 p-4">
                            <DemographicPopoverList timeslot={timeslot} experience={experience} />
                        </Popover.Content>
                    </Popover>
                </span>

                {holdStatusCount > 0 && (
                    <span className={clsx(scope === DashboardScope.WAITLIST && "sm:hidden xl:inline-block")}>
                        <span className="mr-2 h-1.5 w-1.5 text-[#CDC9D3]">&bull;</span>
                        <span data-testid="ui-dashboard-on-hold-count">
                            <var>{getHoldStatusCount(timeslot.statusCounts)}</var> Purchasing
                        </span>
                    </span>
                )}

                <IsGranted permissions={Permission.WAITLISTS_VIEW}>
                    {hasWaitlists(timeslot) ? (
                        <span
                            className={clsx(
                                "ui-dashboard-waitlist",
                                holdStatusCount && scope !== DashboardScope.WAITLIST && "sm:hidden xl:inline-block",
                            )}
                        >
                            <span className="mr-2 h-1.5 w-1.5 text-[#CDC9D3]">&bull;</span>
                            <span>
                                <var data-var="waitlistCount">{timeslot.waitlistCount}</var> Waitlisted
                            </span>
                        </span>
                    ) : null}
                </IsGranted>
            </div>

            {/* Private, virtual & aggregated balance */}
            <div className="flex h-5 space-x-2">
                {aggregatedBalance > 0 && (
                    <IsGranted permissions={Permission.AMOUNT_DUE_VIEW}>
                        <BalanceBadge value={aggregatedBalance} className="visible" />
                    </IsGranted>
                )}

                {pendingCount > 0 && <PendingBadgeCount pendingCount={pendingCount} />}

                <WifiBadge visible={isVirtual} />

                {isPrivate ? (
                    purchaseItemStatus === PurchaseItemStatus.CANCELED ? (
                        <Badge
                            color="warning"
                            icon={<LockIcon />}
                            className="ui-dashboard-item-private overflow-x-hidden"
                        >
                            Private Event
                        </Badge>
                    ) : (
                        purchaseItemStatus !== PurchaseItemStatus.HOLD && (
                            <Tooltip content={name ?? ""} disabled={!name} className="inline-flex overflow-x-hidden">
                                <Badge
                                    color="warning"
                                    icon={<LockIcon />}
                                    className="ui-dashboard-item-private overflow-x-hidden"
                                >
                                    <NoTranslate if={!!name} className="overflow-hidden text-ellipsis">
                                        {name ?? "Private Event"}
                                    </NoTranslate>
                                </Badge>
                            </Tooltip>
                        )
                    )
                ) : null}
            </div>
        </div>
    );
};

interface WifiBadgeProps {
    visible: boolean;
}

const WifiBadge = ({ visible }: WifiBadgeProps) => {
    if (!visible) return null;

    return (
        <Badge className="ui-dashboard-virtual">
            <WifiIcon />
        </Badge>
    );
};
